import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    CircularProgress,
    Box,
} from "@mui/material";
import { toast } from "react-toastify";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useLazyGetDDLSystemUserListQuery } from "store/services/userApi";
import { useAddNoteSourceMutation } from "store/services/noteApi";
import { useResubmitVehicleHiringEntryMutation } from "store/services/vehicleHiringEntryApi";

const VehicleHiringEntryResubmitDialog = ({ open, entry, onClose, onSuccess }) => {
    const [noteText, setNoteText] = useState("");
    const [noteError, setNoteError] = useState("");
    const [responsibleUser, setResponsibleUser] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const [fetchUsers, { data: userList, isLoading: loadingUsers }] =
        useLazyGetDDLSystemUserListQuery();
    const [addNoteSource] = useAddNoteSourceMutation();
    const [resubmitEntry] = useResubmitVehicleHiringEntryMutation();

    useEffect(() => {
        if (open) {
            fetchUsers({});
            setNoteText("");
            setNoteError("");
            setResponsibleUser(
                entry?.responsibleUser
                    ? { _id: entry.responsibleUser._id, label: entry.responsibleUser.name }
                    : null
            );
        }
    }, [open, entry, fetchUsers]);

    const mappedUsers =
        userList?.map((user) => ({ _id: user._id, label: user.name })) || [];

    const handleSubmit = async () => {
        if (noteText.trim().length < 3) {
            setNoteError("Note must be at least 3 characters.");
            return;
        }
        setIsSaving(true);
        try {
            await addNoteSource({
                content: `Resubmit Reason: ${noteText.trim()}`,
                type: "Note",
                source: "VehicleHiringEntry",
                sourceRef: entry._id,
            }).unwrap();
            await resubmitEntry({
                id: entry._id,
                responsibleUser: responsibleUser?._id || null,
            }).unwrap();
            toast.success("Entry resubmitted successfully!");
            onSuccess?.();
            onClose();
        } catch (err) {
            toast.error(err?.data?.message ?? "Failed to resubmit entry");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Resubmit Entry</DialogTitle>
            <DialogContent>
                <Box mt={1} mb={2}>
                    <BaseAutocomplete
                        fullWidth
                        label="Approve / Submitted To"
                        value={responsibleUser}
                        onChange={(_, data) => setResponsibleUser(data)}
                        getOptionLabel={(opt) => opt?.label ?? ""}
                        isOptionEqualToValue={(option, value) => option?._id === value?._id}
                        options={mappedUsers}
                        loading={loadingUsers}
                        loadingText="Loading Users..."
                    />
                </Box>
                <TextField
                    autoFocus
                    fullWidth
                    multiline
                    rows={3}
                    margin="dense"
                    label="Reason for Resubmission"
                    value={noteText}
                    onChange={(e) => {
                        setNoteText(e.target.value);
                        if (noteError) setNoteError("");
                    }}
                    error={!!noteError}
                    helperText={noteError || "Minimum 3 characters."}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSubmit} disabled={isSaving}>
                    {isSaving ? <CircularProgress size={20} /> : "Submit"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default VehicleHiringEntryResubmitDialog;